import React, { useState } from 'react';
import {
    IonCard,
    IonCardHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonGrid,
    IonRow,
    IonCol,
    IonInput,
    IonText,
    IonCheckbox,
    IonSpinner,
} from '@ionic/react';
import { arrowBack, fingerPrint, mail } from 'ionicons/icons';
import styles from '../../pages/login/LoginReg.module.scss';
import { useTranslation } from 'react-i18next';
import { hasUsedPasskeyOnDevice } from '../../utils/passkeyUtils';

interface LoginCardProps {
    formData: {
        email: string;
    };
    uiState?: {
        rememberUser: boolean;
    };
    isDisabled: boolean;
    onGoBack: () => void;
    onContinue: () => void;
    onInputChange: (field: string, value: string) => void;
    onCheckboxChange?: (field: string, value: boolean) => void;
    onPasskeyLogin?: () => void;
    onPasskeyRegister?: () => void;
}

export const LoginCard: React.FC<LoginCardProps> = ({
    formData,
    uiState,
    isDisabled,
    onGoBack,
    onContinue,
    onInputChange,
    onCheckboxChange,
    onPasskeyLogin,
    onPasskeyRegister
}) => {
  const { t } = useTranslation();
    // Simple state toggle - user manually chooses between password/passkey mode
    const [showPasskeyMode, setShowPasskeyMode] = useState(() => {
        return localStorage.getItem('preferPasskey') === 'true';
    });

    // Add loading state for passkey login
    const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

    // Has the email typed above used a passkey on this device? Per email, so a second account can still create its own
    const hasUsedPasskey = hasUsedPasskeyOnDevice(formData.email);

    const toggleAuthMode = () => {
        const newMode = !showPasskeyMode;
        setShowPasskeyMode(newMode);
        localStorage.setItem('preferPasskey', String(newMode));
    };

    const handlePasskeyLogin = async () => {
        if (!onPasskeyLogin) return;
        setIsPasskeyLoading(true);
        try {
            await onPasskeyLogin();
        } finally {
            setIsPasskeyLoading(false);
        }
    };

    return (
        <IonCard className={`${styles.loginCard} ion-text-center`}>
            <IonCardHeader className={styles.cardHeader}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton
                            fill="clear"
                            shape='round'
                            className={styles.backButton}
                            onClick={onGoBack}
                            disabled={isDisabled}
                        >
                            <IonIcon icon={arrowBack} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle className={styles.cardTitle}>{t('components.AuthComponents.LoginCard.memberLogin')}</IonTitle>
                </IonToolbar>
            </IonCardHeader>
            <IonGrid>
                <IonRow>
                    <IonCol>
                        <form onSubmit={(e) => { e.preventDefault(); onContinue(); }}>
                            <IonInput
                                id="login-email"
                                className={`${styles.customInput} ion-margin-top`}
                                labelPlacement="stacked"
                                label={t('components.AuthComponents.LoginCard.email')}
                                type="email"
                                placeholder="email@domain.com"
                                value={formData.email}
                                onIonInput={(e) => onInputChange('email', e.detail.value!)}
                                disabled={isDisabled}
                                required
                            >
                                <IonIcon icon={mail} slot="start" />
                            </IonInput>

                            {/* Remember me checkbox - moved from PasswordCard */}
                            {uiState && onCheckboxChange && (
                                <div className={`${styles.rememberForgotContainer} ion-margin-vertical`}>
                                    <label className={styles.checkboxContainer}>
                                        <IonCheckbox
                                            checked={uiState.rememberUser}
                                            onIonChange={(e) => onCheckboxChange('rememberUser', e.detail.checked)}
                                            disabled={isDisabled}
                                            className={styles.rememberCheckbox}
                                        />
                                        <span className={styles.rememberLabel}>{t('components.AuthComponents.LoginCard.rememberMe')}</span>
                                    </label>
                                </div>
                            )}

                            <div className={styles.centerCol}>
                                {/* Password mode - show Continue button */}
                                {!showPasskeyMode && (
                                    <>
                                        <IonButton
                                            expand="block"
                                            type="submit"
                                            disabled={isDisabled}
                                            className={`${styles.customButton} ${styles.mainButton}`}
                                        >{t('components.AuthComponents.LoginCard.continue')}
                                        </IonButton>

                                        {/* Show "Use passkey instead?" link */}
                                        {onPasskeyLogin && (
                                            <IonText
                                                color="primary"
                                                className="ion-margin-top ion-text-center"
                                                onClick={toggleAuthMode}
                                            >
                                                <p className="ion-no-margin"><small className={styles.clickableText}>{t('components.AuthComponents.LoginCard.usePasskeyInstead')}</small></p>
                                            </IonText>
                                        )}
                                    </>
                                )}

                                {/* Passkey mode - show Login with Passkey button */}
                                {showPasskeyMode && onPasskeyLogin && (
                                    <>
                                        <IonButton
                                            expand="block"
                                            fill="outline"
                                            onClick={handlePasskeyLogin}
                                            disabled={isDisabled || isPasskeyLoading}
                                            className={styles.customButton}
                                        >
                                            {!isPasskeyLoading && (
                                                <IonIcon icon={fingerPrint} slot="start" />
                                            )}
                                            {isPasskeyLoading ? t('components.AuthComponents.LoginCard.loggingIn') : t('components.AuthComponents.LoginCard.loginWithPasskey')}
                                            {isPasskeyLoading && (
                                                <IonSpinner name="dots" slot="end" />
                                            )}
                                        </IonButton>

                                        {/* Show "Use password instead?" link */}
                                        <IonText
                                            color="primary"
                                            className="ion-margin-top ion-text-center"
                                            onClick={toggleAuthMode}
                                        >
                                            <p className="ion-no-margin"><small className={styles.clickableText}>{t('components.AuthComponents.LoginCard.usePasswordInstead')}</small></p>
                                        </IonText>
                                    </>
                                )}

                                {/* Show "New to passkeys? Create one" link for new users (only if they haven't used passkey before) */}
                                {!hasUsedPasskey && onPasskeyRegister && (
                                    <IonText
                                        color="primary"
                                        className="ion-margin-top ion-text-center"
                                        onClick={onPasskeyRegister}
                                    >
                                        <p className="ion-no-margin"><small className={styles.clickableText}>{t('components.AuthComponents.LoginCard.newToPasskeysCreateOne')}</small></p>
                                    </IonText>
                                )}
                            </div>
                        </form>
                    </IonCol>
                </IonRow>
            </IonGrid>
        </IonCard>
    );
};
